'use client';

import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Loading } from '@jouzen/ecom-components/v3/Loading';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { ReactNode, useState } from 'react';

import { box, page } from '@/app/styles/ui';
import { config, type Partner } from '@/configs/partner';
import type { Address, LineItem } from '@/types';

import {
  HelpEmailLink,
  OrderInfo,
  OrderItems,
  SectionHeading,
  Svg,
} from '../../ui';
import Section from '../Section';

const IOS_APP_STORE_URL = 'https://apps.apple.com/us/app/oura/id1043837948';
const GOOGLE_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.ouraring.oura&hl=en_US';

interface SummaryLayoutProps {
  readonly address: Address;
  readonly email?: string;
  readonly lineItems: LineItem[];
  readonly orderId?: string;
  /**
   * Quantity shown next to the order number. Pass it when lineItems
   * includes rows that aren't rings (e.g. a membership); falls back to
   * the number of lineItems.
   */
  readonly ringCount?: number;
  readonly isLoading: boolean;
  readonly additionalInfo?: ReactNode;
  readonly partner: Partner;
  /**
   * Overrides the default success heading shown at the top of the
   * page. Use when the default copy ("...ring shipment") doesn't fit
   * the flow — e.g. the redeem flow ships sizing kits, not rings.
   * Falls back to `eop_order_confirmation_page_success_text` when
   * omitted, preserving today's behavior for business / essence.
   */
  readonly successText?: ReactNode;
  /**
   * Overrides the order section's heading. Defaults to
   * `eop_order_confirmation_page_title` ("Oura Ring Summary"), which
   * is correct for ring orders but not for the redeem flow's sizing
   * kit summary.
   */
  readonly title?: ReactNode;
}

const SummaryLayout = ({
  address,
  email,
  lineItems,
  orderId,
  ringCount,
  isLoading,
  additionalInfo,
  partner,
  successText,
  title,
}: SummaryLayoutProps) => {
  const t = useTranslations();

  // Capture the confirmation date once, on mount, so it stays stable across
  // re-renders. The order's own `orderDate` isn't populated in the summary
  // redux flow, so this preserves the previous `new Date()` behavior without
  // the per-render nondeterminism.
  const [displayDate] = useState(() => new Date());

  // only populate details if orderId is provided
  const orderDetails = orderId
    ? {
        number: orderId,
        date: displayDate,
        quantity: ringCount ?? lineItems?.length ?? 0,
      }
    : undefined;

  const styles = {
    box: box(),
    grayBox: box({ bg: 'gray' }),
    page: page({ pt: 'standard' }),
  };

  const helpEmail = config[partner].supportEmail;

  return (
    <div>
      <LayoutGrid className={styles.page}>
        <GridItem
          colEnd={{ sm: 'main' }}
          colStart={{ sm: 'main' }}
          className="flex items-center gap-4 rounded-lg border border-gray-300 p-4 md:px-8 md:py-6"
        >
          <div className="shrink-0">
            <Svg name="green-circle-check" />
          </div>

          <SectionHeading Element="h2">
            {successText ?? t('eop_redeem_confirmation_success_text')}
          </SectionHeading>
        </GridItem>
      </LayoutGrid>

      <Section border title={title ?? t('eop_order_confirmation_page_title')}>
        <div
          data-testid="order-display"
          className={cx(styles.box, 'flex flex-col gap-6')}
        >
          <div className={styles.grayBox}>
            <OrderInfo
              className="lg:col-span-2 lg:row-start-1"
              address={address}
              details={orderDetails}
              email={email}
            />
          </div>

          {isLoading ? (
            <Loading className="my-4" />
          ) : (
            <OrderItems lineItems={lineItems} />
          )}
        </div>
      </Section>

      <Section border title={t('eop_order_confirmation_get_app')}>
        <div className="flex flex-col gap-8">
          <Typography Element="section" variant="body-large">
            {t.rich('eop_order_confirmation_app_message', {
              IOSLink: (chunks) => (
                <BodyLink
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  {chunks}
                </BodyLink>
              ),
              GoogleLink: (chunks) => (
                <BodyLink href={GOOGLE_PLAY_STORE_URL} target="_blank">
                  {chunks}
                </BodyLink>
              ),
            })}
          </Typography>

          {additionalInfo}
        </div>
      </Section>

      <Section title={t('eop_need_additional_help_title')}>
        <div className="flex h-full items-center">
          <HelpEmailLink helpEmail={helpEmail} />
        </div>
      </Section>
    </div>
  );
};

export default SummaryLayout;
